/**
 * Глобальні банери під верхньою панеллю (UX-13; layout.md, розд. 1): видно один — найважливіший
 * за `GLOBAL_BANNER` (немає зв'язку → сесія → нова версія → оголошення → пропозиція легкого
 * режиму → розбіжність поясу → старий браузер). Свої банери оболонки —
 * тут, `<Banner area="global">`; банери модулів і подій — `showBanner()` у `BannerSlot`.
 * «Немає зв'язку» — за фактами мережі (`shell/online.ts`, UX-29), «нова версія» — `VersionBanner`
 * (UX-31). Сплив сесії банера не має: це вхід поверх сторінки (`features/relogin`, UX-30).
 */
import { useLocation } from '@tanstack/react-router';
import { useEffect, useLayoutEffect, useState } from 'react';
import { shell as t } from '../i18n/uk/shell.ts';
import { type LiteReason, liteOfferReason, readSignals } from '../prefs/lite-offer.ts';
import { getPrefs } from '../prefs/prefs.ts';
import { Banner, BannerSlot, GLOBAL_BANNER } from '../ui/notify/index.ts';
import { BrowserUpdateGate } from './BrowserUpdateGate.tsx';
import { liteBanner, loadLiteBanner } from './lite-banner.ts';
import { useOnline } from './online.ts';
import { RegionBanner } from './RegionBanner.tsx';
import { VersionBanner } from './VersionBanner.tsx';

const decide = (): LiteReason | null =>
  liteOfferReason(getPrefs(), navigator, readSignals(), Date.now());

interface Offer {
  reason: LiteReason | null;
  /** Банер можна малювати: його чанк уже був на сторінці, коли екран малювався. */
  ready: boolean;
}

const offerNow = (reason: LiteReason | null): Offer => ({
  reason,
  ready: reason !== null && liteBanner() !== null,
});

/**
 * Рішення про пропозицію легкого режиму — до малювання: на старті (у першому рендері) і при
 * переході на інший екран (`useLayoutEffect`, той самий кадр, що й новий екран). Посеред роботи банер
 * не з'являється: він зсунув би вміст (CLS, lite-mode.md, розд. 2). Тому чанк банера починає
 * вантажитись, щойно рішення «так», але малюється банер лише разом з екраном: якщо чанк приїхав
 * після кадру поточного екрана, банер чекає наступного переходу (`pathname`).
 */
export function LiteOfferGate() {
  const pathname = useLocation({ select: (l) => l.pathname });
  const [offer, setOffer] = useState<Offer>(() => offerNow(decide()));
  // biome-ignore lint/correctness/useExhaustiveDependencies: перерішуємо лише на новому екрані
  useLayoutEffect(() => {
    setOffer((prev) => {
      const next = offerNow(prev.reason ?? decide());
      return next.reason === prev.reason && next.ready === prev.ready ? prev : next;
    });
  }, [pathname]);
  useEffect(() => {
    if (offer.reason !== null) void loadLiteBanner();
  }, [offer.reason]);
  const Offer = liteBanner();
  if (!offer.ready || offer.reason === null || Offer === null) return null;
  return <Offer reason={offer.reason} />;
}

export function GlobalBanners() {
  const online = useOnline();
  return (
    <>
      <Banner
        area="global"
        priority={GLOBAL_BANNER.offline}
        when={!online}
        tone="warn"
        icon="wifi-off"
        className="banner--global"
        testId="banner-offline"
      >
        <span>{t.offline}</span>
      </Banner>
      <VersionBanner />
      <LiteOfferGate />
      <RegionBanner />
      <BrowserUpdateGate />
      <BannerSlot area="global" className="banner--global" />
    </>
  );
}
